/** Firebase Auth minimum password length */
export const MIN_FIREBASE_PASSWORD_LENGTH = 6;

const defaultMessages = {
  mismatch: "Lösenorden matchar inte.",
  tooShort: `Lösenordet måste vara minst ${MIN_FIREBASE_PASSWORD_LENGTH} tecken.`,
};

/**
 * Validates password and confirmation before calling Firebase.
 * Returns an error message or null if valid.
 */
export function validatePasswordPair(
  password: string,
  passwordRepeat: string,
  messages: { mismatch: string; tooShort: string } = defaultMessages
): string | null {
  if (password !== passwordRepeat) return messages.mismatch;
  if (password.length < MIN_FIREBASE_PASSWORD_LENGTH) return messages.tooShort;
  return null;
}

type FirebaseErrorMessages = {
  weak: string;
  emailInUse: string;
  invalidEmail: string;
  operationNotAllowed: string;
  tryAgain: string;
  tooManyRequests?: string;
  network?: string;
};

const defaultFirebaseMessages: FirebaseErrorMessages = {
  weak: `Lösenordet är för svagt. Använd minst ${MIN_FIREBASE_PASSWORD_LENGTH} tecken.`,
  emailInUse: "E-postadressen används redan.",
  invalidEmail: "Ogiltig e-postadress.",
  operationNotAllowed: "E-postregistrering är inte aktiverad.",
  tryAgain: "Något gick fel. Försök igen.",
  tooManyRequests: "För många försök. Vänta en stund och försök igen.",
  network:
    "Ingen kontakt med servern. Kontrollera din uppkoppling och försök igen.",
};

export function mapFirebaseCreateUserError(
  error: unknown,
  messages: FirebaseErrorMessages = defaultFirebaseMessages,
): string {
  const code =
    error && typeof error === "object" && "code" in error
      ? String((error as { code?: string }).code)
      : "";

  switch (code) {
    case "auth/weak-password":
      return messages.weak;
    case "auth/email-already-in-use":
      return messages.emailInUse;
    case "auth/invalid-email":
      return messages.invalidEmail;
    case "auth/operation-not-allowed":
      return messages.operationNotAllowed;
    case "auth/too-many-requests":
      return messages.tooManyRequests ?? messages.tryAgain;
    case "auth/network-request-failed":
      return messages.network ?? messages.tryAgain;
    default:
      // Any other Firebase Auth code carries a raw "Firebase: Error (auth/…)"
      // message that must never reach the user.
      if (code.startsWith("auth/")) return messages.tryAgain;
      if (error instanceof Error && error.message) return error.message;
      return messages.tryAgain;
  }
}
